// The inspector's Look Up tab (LookUpViews.swift; the writer reads Dossier, as the Mac's own Look Up is the dictionary): what the project knows about the words looked up, in layers — the
// cast, the glossary, research notes titled after it, notes that mention it, the manuscript's scenes — or, with nothing
// found, the ways to add it; and, at the foot, Pictures from Wikimedia Commons by a button that names the service.
// Look Up is reached from the text's context menu (registered here), Cast & Places and the study page.
import { useEffect, useState } from 'react';
import { entityCSSColor, lookUpQuery, StoreEditor, type DocumentEditor, type Entity, type LookUpAnswer, type LookUpPage, type Project } from '@writing-suite/core';
import { ENTITY_KIND_LABELS, GLOSSARY_KIND_LABELS, PRESENCE_LABELS, studyAnswers } from '@writing-suite/core/story';
import { announce, useApp } from '../shell/context.ts';
import { followLink, formatRoute } from '../shell/route.ts';
import { useWorkspace } from '../shell/workspace.ts';
import type { LiveProject } from '../store/project.ts';
import { clearLookUp, findPictures, keepPicture, lookUp, morePictures, refreshLookUp, useLookUp } from '../store/lookUp.ts';
import { addWordToGlossary } from '../store/revise.ts';
import { storyEdit, storyModel } from '../store/story.ts';
import type { WikiPicture } from '../store/wikimedia.ts';
import { isPicture, useAttachmentURL } from './Attachments.tsx';
import { registerTextMenuItems } from './WritePage.tsx';
import { EntityDot, plural } from './story/common.tsx';

// Look Up “word” first in the text's menu, for a selection within one document.
registerTextMenuItems((ctx) => {
  const sel = ctx.selection;
  const query = sel ? lookUpQuery(sel.text) : null;
  if (!query) return [];
  const label = query.text.length > 24 ? `Dossier on “${query.text.slice(0, 22)}…”` : `Dossier on “${query.text}”`;
  return [{ label, onSelect: () => lookUp(ctx.project, query.text) }];
});

export function LookUpPanel({ project, live, canWrite }: { project: Project; live: LiveProject | null; canWrite: boolean }) {
  const { answer } = useLookUp();
  // The project changed (a page, a picture): the same words answered again.
  useEffect(() => { refreshLookUp(project); }, [project]);
  // What the text's menu does, for the browser test (relay/test/e2e/lookUp.e2e.ts), which has no word to select.
  useEffect(() => {
    (window as unknown as { __lookUp?: (words: string) => boolean }).__lookUp = (words) => lookUp(project, words);
    return () => { delete (window as unknown as { __lookUp?: unknown }).__lookUp; };
  }, [project]);
  return (
    <div className="lookup" data-lookup>
      <LookUpHead answer={answer} />
      {answer ? (
        answer.isEmpty ? <NothingFound project={project} live={live} canWrite={canWrite} answer={answer} /> : (
          <>
            {answer.cast.map((e) => <EntityLayer key={e.id} project={project} live={live} entity={e} />)}
            {answer.glossary ? <GlossaryLayer project={project} entry={answer.glossary} /> : null}
            {answer.pages.length ? <PagesLayer title="Pages" pages={answer.pages} /> : null}
            {answer.notes.length ? <NotesLayer project={project} live={live} notes={answer.notes} /> : null}
            {answer.scenes.length ? <ScenesLayer scenes={answer.scenes} /> : null}
          </>
        )
      ) : (
        <p className="empty-note">Select a word in the text and choose Dossier to see what the project knows about it.</p>
      )}
      {answer ? <WebPicturesSection project={project} live={live} canWrite={canWrite} answer={answer} /> : null}
    </div>
  );
}

function LookUpHead({ answer }: { answer: LookUpAnswer | null }) {
  if (!answer) return null;
  const kind = answer.cast.length > 1 ? 'Several' : answer.cast[0] ? ENTITY_KIND_LABELS[answer.cast[0].kind] : answer.glossary ? 'Word' : answer.pages.length ? 'Note' : answer.isEmpty ? 'Nothing found' : 'Mentioned';
  return (
    <div className="lookup-head">
      <span className="lookup-chip" data-lookup-chip>
        <span className="lookup-chip-words">{answer.query.text}</span>
        <span className="lookup-chip-kind">{kind}</span>
      </span>
      <button type="button" className="quiet-button" onClick={clearLookUp} aria-label="Clear">×</button>
    </div>
  );
}

const castHref = (project: Project, live: LiveProject | null, who: string) =>
  formatRoute({ name: 'project', projectId: live?.id ?? project.id, room: 'plan', page: 'cast', nodeId: null, arrangementId: null, params: { who } });

function EntityLayer({ project, live, entity }: { project: Project; live: LiveProject | null; entity: Entity & { studyID?: string; role?: string; notes?: string; latitude?: number; longitude?: number } }) {
  const ws = useWorkspace();
  const page = entity.studyID ? project.fragment(entity.studyID) : null;
  const answers = page ? studyAnswers(page.text).slice(0, 4) : [];
  const portrait = useAttachmentURL(live, page?.attachments.find(isPicture) ?? null);
  const seen = countAppearances(project, entity.id);
  const role = entity.role?.trim()
    ? entity.role.trim()[0].toUpperCase() + entity.role.trim().slice(1)
    : entity.kind === 'place' && entity.notes?.trim() ? entity.notes.trim().split('\n')[0]
    : seen === 0 ? 'Not in the manuscript yet' : `In ${plural(seen, 'scene')}`;
  const href = castHref(project, live, entity.id);
  return (
    <section className="lookup-layer lookup-entity" data-entity-id={entity.id}>
      <div className="lookup-entity-head">
        <div className="study-portrait small" aria-hidden="true" ref={(el) => el?.style.setProperty('--dot', entityCSSColor(entity.color))}>
          {portrait.url ? <img src={portrait.url} alt="" /> : <EntityDot color={entity.color} size="large" />}
        </div>
        <div>
          <h3 className="lookup-name">{entity.name}</h3>
          <p className="lookup-role">{role}</p>
          {entity.aliases.length ? <p className="field-note">Also called {entity.aliases.join(', ')}</p> : null}
        </div>
      </div>
      {entity.kind === 'place' ? (
        entity.latitude !== undefined && entity.longitude !== undefined ? (
          <p className="field-note">
            {coordinates(entity.latitude, entity.longitude)}{' '}
            <a href={`https://maps.apple.com/?ll=${entity.latitude},${entity.longitude}&q=${encodeURIComponent(entity.name)}`} target="_blank" rel="noopener noreferrer">Show in Maps</a>
          </p>
        ) : <p className="field-note">Not on the map yet.</p>
      ) : null}
      {answers.length ? (
        <div className="lookup-answers">
          {answers.map((a, i) => (
            <p key={i} className="study-answer"><strong>{a.label}.</strong> {a.text.slice(0, 240)}</p>
          ))}
        </div>
      ) : entity.studyID ? <p className="field-note">Nothing on the study page yet.</p> : null}
      <p className="lookup-actions">
        {entity.studyID && ws ? (
          <button type="button" className="link-button" onClick={() => ws.reveal({ fragmentId: entity.studyID! })}>Open Study Page</button>
        ) : null}
        <a className="link-button" href={href} onClick={(e) => followLink(e, href)}>Edit Details</a>
      </p>
    </section>
  );
}

function countAppearances(project: Project, entityId: string): number {
  let n = 0;
  const manuscript = project.manuscriptId();
  if (!manuscript) return 0;
  const inManuscript = new Set<string>();
  const walk = (nodes: { fragmentId: string | null; children: any[] }[]) => { for (const x of nodes) { if (x.fragmentId) inManuscript.add(x.fragmentId); walk(x.children); } };
  walk(project.tree(manuscript));
  for (const row of storyModel(project).rows) if (row.entityID === entityId && row.level !== 'absent' && inManuscript.has(row.fragmentID)) n++;
  return n;
}

const coordinates = (lat: number, lon: number) => `${Math.abs(lat).toFixed(2)}° ${lat < 0 ? 'S' : 'N'}, ${Math.abs(lon).toFixed(2)}° ${lon < 0 ? 'W' : 'E'}`;

function GlossaryLayer({ project, entry }: { project: Project; entry: LookUpAnswer['glossary'] & object }) {
  const ws = useWorkspace();
  const href = formatRoute({ name: 'project', projectId: ws?.live?.id ?? project.id, room: 'plan', page: 'glossary', nodeId: null, arrangementId: null, params: {} });
  return (
    <section className="lookup-layer">
      <h3 className="eyebrow">Glossary</h3>
      <p className="lookup-name">{entry.term} <span className="lookup-chip-kind">{GLOSSARY_KIND_LABELS[entry.kind]}</span></p>
      {entry.variants.length ? <p className="field-note">Also spelled {entry.variants.join(', ')}</p> : null}
      {entry.note ? <p className="study-answer">{entry.note}</p> : null}
      <p className="lookup-actions"><a className="link-button" href={href} onClick={(e) => followLink(e, href)}>Edit in Glossary</a></p>
    </section>
  );
}

function PageRow({ page, detail }: { page: LookUpPage; detail?: string | null }) {
  const ws = useWorkspace();
  return (
    <li>
      <button type="button" className="lookup-row" onClick={() => ws?.reveal({ fragmentId: page.id })} data-fragment-id={page.id}>
        <span>{page.title || 'Untitled'}</span>
        {detail ? <span className="column-count">{detail}</span> : null}
      </button>
    </li>
  );
}

function PagesLayer({ title, pages }: { title: string; pages: LookUpPage[] }) {
  return (
    <section className="lookup-layer">
      <h3 className="eyebrow">{title}</h3>
      <ul className="lookup-rows">{pages.map((p) => <PageRow key={p.id} page={p} />)}</ul>
    </section>
  );
}

function NotesLayer({ project, live, notes }: { project: Project; live: LiveProject | null; notes: LookUpAnswer['notes'] }) {
  const ws = useWorkspace();
  const pictures = notes.slice(0, 12).flatMap((n) => (project.fragment(n.page.id)?.attachments ?? []).filter(isPicture).map((a) => ({ a, page: n.page }))).slice(0, 12);
  return (
    <section className="lookup-layer">
      <h3 className="eyebrow">Research</h3>
      {pictures.length ? (
        <div className="lookup-pictures">
          {pictures.map(({ a, page }) => <AttachmentThumb key={a.sha256} live={live} sha256={a.sha256} mimeType={a.mimeType} title={page.title} onOpen={() => ws?.reveal({ fragmentId: page.id })} />)}
        </div>
      ) : null}
      <ul className="lookup-rows">
        {notes.map((n) => (
          <li key={n.page.id}>
            <button type="button" className="lookup-row stacked" onClick={() => ws?.reveal({ fragmentId: n.page.id })} data-fragment-id={n.page.id}>
              <span>{n.page.title || 'Untitled'}</span>
              {n.snippet ? <Snippet text={n.snippet} /> : null}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function AttachmentThumb({ live, sha256, mimeType, title, onOpen }: { live: LiveProject | null; sha256: string; mimeType: string; title: string; onOpen: () => void }) {
  const { url } = useAttachmentURL(live, { sha256, filename: '', mimeType });
  if (!url) return null;
  return (
    <button type="button" className="lookup-picture" onClick={onOpen} aria-label={title}>
      <img src={url} alt="" />
    </button>
  );
}

/** The snippet's `[hits]` drawn bold. */
function Snippet({ text }: { text: string }) {
  const parts = text.split(/[[\]]/);
  return <span className="lookup-snippet">{parts.map((p, i) => (i % 2 ? <strong key={i}>{p}</strong> : <span key={i}>{p}</span>))}</span>;
}

function ScenesLayer({ scenes }: { scenes: LookUpAnswer['scenes'] }) {
  return (
    <section className="lookup-layer">
      <h3 className="eyebrow">Scenes</h3>
      <ul className="lookup-rows">
        {scenes.map((s) => <PageRow key={s.page.id} page={s.page} detail={s.level === 'mentioned' ? null : PRESENCE_LABELS[s.level]} />)}
      </ul>
    </section>
  );
}

function NothingFound({ project, live, canWrite, answer }: { project: Project; live: LiveProject | null; canWrite: boolean; answer: LookUpAnswer }) {
  const ws = useWorkspace();
  const words = answer.query.text;
  const addToCast = (kind: Entity['kind']) => {
    if (!live) return;
    const made = storyEdit(live, (e) => {
      const entity = e.createEntity(words, kind);
      e.ensureStudyPages();
      return entity;
    });
    announce(`${made.name} added to the cast`);
    refreshLookUp(project);
  };
  const newNote = () => {
    if (!live) return;
    const id = live.edit((e) => {
      const d = e as DocumentEditor;
      return new StoreEditor(d.docs, d.context).addDraft({ title: words, text: '', notes: '' });
    });
    refreshLookUp(project);
    ws?.reveal({ fragmentId: id });
  };
  return (
    <section className="lookup-layer">
      <p className="empty-note">Nothing in the project about “{words}” yet.</p>
      {canWrite && live ? (
        <div className="lookup-adds">
          <span className="lookup-add-label">Add to Cast:</span>
          {(['character', 'place', 'object', 'motif'] as const).map((k) => (
            <button key={k} type="button" className="quiet-button" onClick={() => addToCast(k)}>{ENTITY_KIND_LABELS[k]}</button>
          ))}
          <button type="button" className="quiet-button" onClick={() => { if (addWordToGlossary(live, words)) refreshLookUp(project); }}>Add to Glossary</button>
          <button type="button" className="quiet-button" onClick={newNote}>New Research Note</button>
        </div>
      ) : null}
    </section>
  );
}

function WebPicturesSection({ project, live, canWrite, answer }: { project: Project; live: LiveProject | null; canWrite: boolean; answer: LookUpAnswer }) {
  const { web } = useLookUp();
  const app = useApp();
  const ws = useWorkspace();
  const [error, setError] = useState<string | null>(null);
  const locale = project.locale;
  const keepsWhere = answer.cast.find((e) => e.studyID)
    ? `Keep puts a picture on ${answer.cast.find((e) => e.studyID)!.name}’s study page, with its credit in the page’s notes.`
    : `Keep puts a picture in a new note in the Inbox of Research & Notes, titled “${answer.query.text}”, with its credit in the note’s notes.`;
  const keep = async (picture: WikiPicture) => {
    const api = app.account?.session?.api;
    if (!live || !api) { setError('Sign in to keep a picture.'); return; }
    setError(await keepPicture(live, api, project, picture));
  };
  return (
    <section className="lookup-layer lookup-web" data-lookup-web>
      <h3 className="eyebrow">From the web</h3>
      {web.query === null ? (
        <>
          <button type="button" className="quiet-button" onClick={() => void findPictures(locale)} data-find-pictures>Pictures from Wikimedia Commons</button>
          <p className="field-note">Sends “{answer.query.text}” to Wikimedia, once, when you choose it. Nothing is kept until you choose Keep.</p>
        </>
      ) : (
        <>
          {!web.pictures.length && !web.loading && !web.error ? <p className="field-note">Wikimedia has no pictures for “{answer.query.text}”.</p> : null}
          <div className="lookup-web-grid">
            {web.pictures.map((p) => (
              <figure key={p.id} className="lookup-web-picture" data-picture-id={p.id}>
                <img src={p.thumbnailURL} alt={p.title} loading="lazy" />
                <figcaption>
                  <span className="lookup-web-title">{p.title}</span>
                  <span className="field-note">{[p.fromArticle ? 'From the article' : null, p.licence || 'Licence not stated', p.author || null].filter(Boolean).join(' · ')}</span>
                  <span className="lookup-web-actions">
                    {web.kept.has(p.id) ? (
                      <>
                        <span className="field-note">✓ Kept</span>
                        {ws && web.keptIn.get(p.id) ? <button type="button" className="link-button" onClick={() => ws.reveal({ fragmentId: web.keptIn.get(p.id)! })}>Show</button> : null}
                      </>
                    )
                      : web.keeping.has(p.id) ? <span className="field-note">Keeping…</span>
                      : canWrite && live ? <button type="button" className="link-button" onClick={() => void keep(p)} data-keep>Keep</button> : null}
                    <a href={p.pageURL} target="_blank" rel="noopener noreferrer" className="field-note">On Commons</a>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
          {web.error ? <p className="field-note error">{web.error}</p> : null}
          {error ? <p className="field-note error">{error}</p> : null}
          <p className="lookup-actions">
            {web.loading ? <span className="field-note">Loading…</span> : web.nextOffset !== null ? <button type="button" className="link-button" onClick={() => void morePictures(locale)}>More</button> : null}
            {!web.loading && web.error ? <button type="button" className="link-button" onClick={() => void (web.pictures.length ? morePictures(locale) : findPictures(locale))}>Try Again</button> : null}
          </p>
          <p className="field-note">{keepsWhere}</p>
        </>
      )}
    </section>
  );
}
